import { faMagicWandSparkles } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "src/base-components/Button";
import { casesKeys } from "src/cases/queries/cases";
import {
  CaseDeserialized,
  TaskIdExtractor,
} from "src/cases/queries/casesTypes";
import { usePatchTaskHint } from "src/cases/queries/hints";
import {
  ActionTriggeredEventDataActionFormDataValue,
  CaseTemplateFormFieldsInner,
} from "src/clients/case-management/api";
import { useParamsDecode } from "src/utils/useParamsDecode";

const generateFakeValue = (
  field: CaseTemplateFormFieldsInner,
): ActionTriggeredEventDataActionFormDataValue => {
  switch (field.type) {
    case "text": {
      const repeatCount = Math.floor(Math.random() * 4) + 2;
      return Array.from({ length: repeatCount }, () => "Sample text").join(" ");
    }
    case "number":
      return Math.round(Math.random() * 10000 * 100) / 100;
    case "integer":
      return Math.floor(Math.random() * 100) + 1;
    case "select": {
      const options = field.options;
      return options[Math.floor(Math.random() * options.length)];
    }
    case "multi_select": {
      const shuffled = [...field.options].sort(() => Math.random() - 0.5);
      const count = Math.floor(Math.random() * 2) + 1;
      return shuffled.slice(0, count);
    }
    case "date":
      return new Date().toISOString();
  }
};

type DevSeedTaskHintsButtonProps = {
  caseData: CaseDeserialized;
};

export const DevSeedTaskHintsButton: React.FC<DevSeedTaskHintsButtonProps> = ({
  caseData,
}) => {
  const { wsId } = useParamsDecode<{ wsId: string }>();
  const queryClient = useQueryClient();
  const { mutateAsync: patchTaskHint, isPending } = usePatchTaskHint();

  const caseRecord = caseData?.data;
  const caseId = caseRecord?.id;
  const caseTasks = caseRecord?.case_tasks;
  const { status, sub_status } = caseRecord?.case_properties ?? {};
  const templateVersion = caseRecord?.template_version;

  const availableTasks = (caseTasks ?? []).filter((task) => {
    const taskIdParts = task.id.split("#");
    if (taskIdParts.length !== 3) return false;

    const [taskCaseId, taskStatus] = taskIdParts;
    const expectedStatus = sub_status ? `${status}-${sub_status}` : status;

    return taskCaseId === caseId && taskStatus === expectedStatus;
  });

  const handleSeedHints = async () => {
    if (!caseId) return;

    for (const task of availableTasks) {
      const taskKey = TaskIdExtractor.getStatusAndTaskKey(task.id);
      const templateTaskKey = TaskIdExtractor.getTaskKey(task.id);
      const templateTask = templateVersion?.case_tasks?.[templateTaskKey];
      const fields = templateTask?.form?.fields;

      if (!fields) continue;

      const attributes: Record<
        string,
        ActionTriggeredEventDataActionFormDataValue
      > = {};
      for (const field of fields) {
        attributes[field.key] = generateFakeValue(field);
      }

      await patchTaskHint({
        caseId,
        taskKey,
        hintId: task.id,
        attributes,
      });
    }

    queryClient.invalidateQueries({
      queryKey: casesKeys.get(wsId, caseId),
    });
  };

  return (
    <Button
      dataLoc="dev-seed-task-hints"
      disabled={availableTasks.length === 0}
      iconLeft={faMagicWandSparkles}
      loading={isPending}
      size="sm"
      variant="secondary"
      onClick={handleSeedHints}
    >
      Seed Task Hints
    </Button>
  );
};
